import React, { useState, useMemo } from "react";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  ScrollView,
  type StyleProp,
  type ViewStyle,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { ThemedInput } from "@/components/ThemedInput";
import { EntityIcon } from "@/components/ui/EntityIcon";
import { useThemeColor } from "@/hooks/useThemeColor";
import {
  IUnifiedFilter,
  IFilteredCountsSummary,
  MemoryModel,
  MediaModel,
  Contact,
  ContactModel,
  GroupModel,
  PlaceModel,
  AnchorModel,
  ShareModel,
  ITag,
} from "common247";
import {
  countActiveFilterRules,
  createDefaultFilter,
} from "@/services/unified-filter-service";
import { FilterChip } from "./FilterChip";
import { MemoryFilter } from "./MemoryFilter";
import { MediaFilter } from "./MediaFilter";
import { ContactFilter } from "./ContactFilter";
import { GroupFilter } from "./GroupFilter";
import { PlaceFilter } from "./PlaceFilter";
import { AnchorFilter } from "./AnchorFilter";
import { TagFilter } from "./TagFilter";
import { ShareFilter } from "./ShareFilter";
import { ActivityFilter } from "./ActivityFilter";
import { MessageFilter } from "./MessageFilter";

export type FilterScreenType =
  | "memories"
  | "media"
  | "contacts"
  | "groups"
  | "places"
  | "anchors"
  | "shares"
  | "activity"
  | "messages";

export interface ICollapsibleFilterSectionProps {
  /** Screen type determining primary focus and which specialized filters are exposed */
  screen: FilterScreenType;
  /** Active unified filter */
  filter: IUnifiedFilter;
  /** Callback triggered whenever any filter criteria changes */
  onFilterChange: (updated: IUnifiedFilter) => void;
  /** Calculated summary of items filtered */
  counts?: IFilteredCountsSummary;
  /** Callback to clear/reset filters */
  onReset?: () => void;
  /** Available entities for relationship selection */
  allMemories?: MemoryModel[];
  allMedia?: MediaModel[];
  allContacts?: (Contact | ContactModel)[];
  allGroups?: GroupModel[];
  allPlaces?: PlaceModel[];
  allAnchors?: AnchorModel[];
  allShares?: ShareModel[];
  allTags?: ITag[];
  /** Optional custom container style */
  style?: StyleProp<ViewStyle>;
}

type FilterTabKey =
  | "memory"
  | "media"
  | "contact"
  | "group"
  | "place"
  | "anchor"
  | "tag"
  | "share"
  | "activity"
  | "message";

/**
 * Standardized Collapsible Filter Section for all entity lists.
 * - Defaults to collapsed.
 * - When collapsed, shows a summary of items filtered: total count, memories, media, contacts, groups.
 * - When expanded, provides child filter components: MemoryFilter, MediaFilter, ContactFilter,
 *   GroupFilter, TagFilter, ShareFilter, ActivityFilter (activity only), MessageFilter (messages only).
 */
export function CollapsibleFilterSection({
  screen,
  filter,
  onFilterChange,
  counts = { total: 0, memories: 0, media: 0, contacts: 0, groups: 0 },
  onReset,
  allMemories = [],
  allMedia = [],
  allContacts = [],
  allGroups = [],
  allPlaces = [],
  allAnchors = [],
  allTags = [],
  style,
}: ICollapsibleFilterSectionProps): React.JSX.Element {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  // Determine initial active tab based on current screen
  const defaultTab: FilterTabKey =
    screen === "anchors"
      ? "anchor"
      : screen === "places"
      ? "place"
      : screen === "media"
      ? "media"
      : screen === "contacts"
      ? "contact"
      : screen === "groups"
      ? "group"
      : screen === "shares"
      ? "share"
      : screen === "activity"
      ? "activity"
      : screen === "messages"
      ? "message"
      : "memory";

  const [activeTab, setActiveTab] = useState<FilterTabKey>(defaultTab);

  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const tintColor = useThemeColor({}, "tint");
  const badgeBg = useThemeColor({}, "buttonSecondary");
  const textMuted = useThemeColor({}, "textMuted");

  const activeRulesCount = countActiveFilterRules(filter);

  // Screen-specific primary entity count
  const primaryCount = useMemo(() => {
    if (!counts) return 0;
    switch (screen) {
      case "memories":
        return counts.memories ?? 0;
      case "media":
        return counts.media ?? 0;
      case "contacts":
        return counts.contacts ?? 0;
      case "groups":
        return counts.groups ?? 0;
      case "places":
        return counts.places ?? counts.total ?? 0;
      case "anchors":
        return counts.anchors ?? counts.total ?? 0;
      case "shares":
        return counts.shares ?? 0;
      case "activity":
        return counts.activities ?? 0;
      case "messages":
        return counts.messages ?? 0;
      default:
        return counts.total ?? 0;
    }
  }, [screen, counts]);

  const handleReset = (): void => {
    if (onReset) {
      onReset();
    } else {
      onFilterChange(createDefaultFilter());
    }
  };

  // Determine available tabs based on screen context
  const availableTabs: { key: FilterTabKey; label: string; icon: any }[] = [
    { key: "memory", label: "Memories", icon: "book-outline" },
    { key: "media", label: "Media", icon: "images-outline" },
    { key: "contact", label: "Contacts", icon: "person-outline" },
    { key: "group", label: "Groups", icon: "people-outline" },
    { key: "place", label: "Places", icon: "location-outline" },
    { key: "anchor", label: "Anchors", icon: "boat-outline" },
    { key: "tag", label: "Tags", icon: "pricetag-outline" },
    { key: "share", label: "Shares", icon: "share-social-outline" },
  ];

  // Activity filter only exposed on activity list
  if (screen === "activity") {
    availableTabs.push({ key: "activity", label: "Activity", icon: "pulse-outline" });
  }

  // Message filter only exposed on messages list
  if (screen === "messages") {
    availableTabs.push({ key: "message", label: "Messages", icon: "chatbubble-ellipses-outline" });
  }

  return (
    <View style={[styles.container, { backgroundColor: cardBg, borderColor }, style]}>
      {/* Collapsed / Toggle Header Bar */}
      <View style={styles.headerBar}>
        <TouchableOpacity
          style={styles.summaryRow}
          onPress={() => setIsExpanded((prev) => !prev)}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Toggle filter section"
          testID="filter-collapsible-toggle"
        >
          {/* Left: Filter title and active rules badge */}
          <View style={styles.titleWithBadge}>
            <Ionicons name="filter-outline" size={16} color={tintColor} />
            <ThemedText style={styles.headerTitle}>Filters</ThemedText>
            {activeRulesCount > 0 && (
              <View style={[styles.activeBadge, { backgroundColor: tintColor }]}>
                <ThemedText style={styles.activeBadgeText}>{activeRulesCount}</ThemedText>
              </View>
            )}
          </View>

          {/* Center: Summary of items filtered */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.countsScrollContainer}
          >
            {/* Total Count for current screen */}
            <View style={[styles.countChip, styles.totalChip, { backgroundColor: `${tintColor}20`, borderColor: tintColor }]}>
              {screen === "memories" && <EntityIcon type="memory" size={12} color={tintColor} style={{ marginRight: 4 }} />}
              {screen === "media" && <EntityIcon type="media" size={12} color={tintColor} style={{ marginRight: 4 }} />}
              {screen === "contacts" && <EntityIcon type="contact" size={12} color={tintColor} style={{ marginRight: 4 }} />}
              {screen === "groups" && <EntityIcon type="group" size={12} color={tintColor} style={{ marginRight: 4 }} />}
              {screen === "places" && <EntityIcon type="place" size={12} color={tintColor} style={{ marginRight: 4 }} />}
              {screen === "anchors" && <EntityIcon type="anchor" size={12} color={tintColor} style={{ marginRight: 4 }} />}
              {screen === "shares" && <EntityIcon type="share" size={12} color={tintColor} style={{ marginRight: 4 }} />}
              {screen === "activity" && <Ionicons name="pulse" size={12} color={tintColor} style={{ marginRight: 4 }} />}
              {screen === "messages" && <Ionicons name="chatbubble" size={12} color={tintColor} style={{ marginRight: 4 }} />}
              <ThemedText style={[styles.countText, styles.totalText, { color: tintColor }]}>
                Total: {primaryCount}
              </ThemedText>
            </View>

            {/* Breakdown Chips (Only on multi-entity/search screens without a single scoped screen context) */}
            {!screen && (
              <>
                {counts?.memories !== undefined && (
                  <View style={[styles.countChip, { backgroundColor: badgeBg, borderColor }]}>
                    <EntityIcon type="memory" size={12} color={tintColor} style={{ marginRight: 3 }} />
                    <ThemedText style={styles.countText}>{counts.memories}</ThemedText>
                  </View>
                )}
                {counts?.media !== undefined && (
                  <View style={[styles.countChip, { backgroundColor: badgeBg, borderColor }]}>
                    <EntityIcon type="media" size={12} color={tintColor} style={{ marginRight: 3 }} />
                    <ThemedText style={styles.countText}>{counts.media}</ThemedText>
                  </View>
                )}
                {counts?.contacts !== undefined && (
                  <View style={[styles.countChip, { backgroundColor: badgeBg, borderColor }]}>
                    <EntityIcon type="contact" size={12} color={tintColor} style={{ marginRight: 3 }} />
                    <ThemedText style={styles.countText}>{counts.contacts}</ThemedText>
                  </View>
                )}
                {counts?.groups !== undefined && (
                  <View style={[styles.countChip, { backgroundColor: badgeBg, borderColor }]}>
                    <EntityIcon type="group" size={12} color={tintColor} style={{ marginRight: 3 }} />
                    <ThemedText style={styles.countText}>{counts.groups}</ThemedText>
                  </View>
                )}
                {counts?.places !== undefined && (
                  <View style={[styles.countChip, { backgroundColor: badgeBg, borderColor }]}>
                    <EntityIcon type="place" size={12} color={tintColor} style={{ marginRight: 3 }} />
                    <ThemedText style={styles.countText}>{counts.places}</ThemedText>
                  </View>
                )}
                {counts?.anchors !== undefined && (
                  <View style={[styles.countChip, { backgroundColor: badgeBg, borderColor }]}>
                    <EntityIcon type="anchor" size={12} color={tintColor} style={{ marginRight: 3 }} />
                    <ThemedText style={styles.countText}>{counts.anchors}</ThemedText>
                  </View>
                )}
                {counts?.shares !== undefined && (
                  <View style={[styles.countChip, { backgroundColor: badgeBg, borderColor }]}>
                    <EntityIcon type="share" size={12} color={tintColor} style={{ marginRight: 3 }} />
                    <ThemedText style={styles.countText}>{counts.shares}</ThemedText>
                  </View>
                )}
                {counts?.activities !== undefined && (
                  <View style={[styles.countChip, { backgroundColor: badgeBg, borderColor }]}>
                    <Ionicons name="pulse" size={12} color={tintColor} style={{ marginRight: 3 }} />
                    <ThemedText style={styles.countText}>{counts.activities}</ThemedText>
                  </View>
                )}
                {counts?.messages !== undefined && (
                  <View style={[styles.countChip, { backgroundColor: badgeBg, borderColor }]}>
                    <Ionicons name="chatbubble" size={12} color={tintColor} style={{ marginRight: 3 }} />
                    <ThemedText style={styles.countText}>{counts.messages}</ThemedText>
                  </View>
                )}
              </>
            )}
          </ScrollView>

          {/* Right: Reset Action + Chevron */}
          <View style={styles.rightActions}>
            {activeRulesCount > 0 && (
              <TouchableOpacity
                onPress={(e) => {
                  e.stopPropagation?.();
                  handleReset();
                }}
                style={styles.resetBtn}
                accessibilityRole="button"
                accessibilityLabel="Reset all filters"
                testID="filter-reset-button"
              >
                <ThemedText style={[styles.resetText, { color: tintColor }]}>Reset</ThemedText>
              </TouchableOpacity>
            )}

            <Ionicons
              name={isExpanded ? "chevron-up" : "chevron-down"}
              size={18}
              color={tintColor}
              style={{ marginLeft: 6 }}
            />
          </View>
        </TouchableOpacity>
      </View>

      {/* Expanded Filter Panel */}
      {isExpanded && (
        <View style={[styles.expandedPanel, { borderTopColor: borderColor }]}>
          {/* Global Search Input */}
          <View style={styles.searchSection}>
            <ThemedInput
              value={filter.searchQuery || ""}
              onChangeText={(txt) => onFilterChange({ ...filter, searchQuery: txt })}
              placeholder="Filter by keyword across names, titles, descriptions..."
              style={styles.searchInput}
            />
          </View>

          {/* Filter Sub-component Tabs */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.tabsScroll}
            contentContainerStyle={styles.tabsContainer}
          >
            {availableTabs.map((tab) => (
              <FilterChip
                key={tab.key}
                label={tab.label}
                icon={tab.icon}
                selected={activeTab === tab.key}
                onPress={() => setActiveTab(tab.key)}
                compact
              />
            ))}
          </ScrollView>

          {/* Active Tab Filter Content */}
          <View style={styles.tabContent}>
            {activeTab === "memory" && (
              <MemoryFilter
                criteria={filter.memoryFilter || {}}
                onChange={(mf) => onFilterChange({ ...filter, memoryFilter: mf })}
                allContacts={allContacts}
                allGroups={allGroups}
              />
            )}

            {activeTab === "media" && (
              <MediaFilter
                criteria={filter.mediaFilter || {}}
                onChange={(medF) => onFilterChange({ ...filter, mediaFilter: medF })}
                allMemories={allMemories}
                allContacts={allContacts}
                allGroups={allGroups}
              />
            )}

            {activeTab === "contact" && (
              <ContactFilter
                criteria={filter.contactFilter || {}}
                onChange={(cf) => onFilterChange({ ...filter, contactFilter: cf })}
                allGroups={allGroups}
              />
            )}

            {activeTab === "group" && (
              <GroupFilter
                criteria={filter.groupFilter || {}}
                onChange={(gf) => onFilterChange({ ...filter, groupFilter: gf })}
                allContacts={allContacts}
              />
            )}

            {activeTab === "place" && (
              <PlaceFilter
                criteria={filter.placeFilter || {}}
                onChange={(pf) => onFilterChange({ ...filter, placeFilter: pf })}
                allContacts={allContacts}
                allGroups={allGroups}
                allMemories={allMemories}
              />
            )}

            {activeTab === "anchor" && (
              <AnchorFilter
                criteria={filter.anchorFilter || {}}
                onChange={(ancF) => onFilterChange({ ...filter, anchorFilter: ancF })}
                allContacts={allContacts}
                allGroups={allGroups}
                allMemories={allMemories}
                allPlaces={allPlaces}
              />
            )}

            {activeTab === "tag" && (
              <TagFilter
                criteria={filter.tagFilter || {}}
                onChange={(tf) => onFilterChange({ ...filter, tagFilter: tf })}
                availableTags={allTags}
              />
            )}

            {activeTab === "share" && (
              <ShareFilter
                criteria={filter.shareFilter || {}}
                onChange={(sf) => onFilterChange({ ...filter, shareFilter: sf })}
              />
            )}

            {activeTab === "activity" && screen === "activity" && (
              <ActivityFilter
                criteria={filter.activityFilter || {}}
                onChange={(af) => onFilterChange({ ...filter, activityFilter: af })}
              />
            )}

            {activeTab === "message" && screen === "messages" && (
              <MessageFilter
                criteria={filter.messageFilter || {}}
                onChange={(msgF) => onFilterChange({ ...filter, messageFilter: msgF })}
              />
            )}
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 10,
    borderWidth: 1,
    marginHorizontal: 16,
    marginVertical: 8,
    overflow: "hidden",
    ...Platform.select({
      web: {
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
      },
    }),
  },
  headerBar: {
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  summaryRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  titleWithBadge: {
    flexDirection: "row",
    alignItems: "center",
    marginRight: 8,
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: "700",
    marginLeft: 6,
  },
  activeBadge: {
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 1,
    marginLeft: 6,
  },
  activeBadgeText: {
    color: "#FFFFFF",
    fontSize: 10,
    fontWeight: "700",
  },
  countsScrollContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 4,
  },
  countChip: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
    borderWidth: 1,
    marginRight: 6,
  },
  totalChip: {
    fontWeight: "700",
  },
  totalText: {
    fontWeight: "700",
  },
  countText: {
    fontSize: 11,
    fontWeight: "600",
  },
  rightActions: {
    flexDirection: "row",
    alignItems: "center",
    marginLeft: 6,
  },
  resetBtn: {
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  resetText: {
    fontSize: 12,
    fontWeight: "600",
  },
  expandedPanel: {
    borderTopWidth: 1,
    padding: 12,
  },
  searchSection: {
    marginBottom: 8,
  },
  searchInput: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    fontSize: 13,
  },
  tabsScroll: {
    marginBottom: 8,
  },
  tabsContainer: {
    flexDirection: "row",
    paddingVertical: 2,
  },
  tabContent: {
    paddingTop: 4,
  },
});
